import React, { useState, useEffect, useCallback } from "react";
import { CalendarIcon, CloseIcon, SpinnerIcon } from "../../Icons";
import { request } from "../../utils/api";
import toast from "react-hot-toast";
import { assignUserAvailabilityBodyRequest } from "../../utils/userAvailability.validation";
import {
  COVERAGE_BTN,
  COVERAGE_CHECKBOX,
  COVERAGE_MODAL,
} from "../../pages/coverageAreaTheme";
import {
  CUSTOM_DAY_OPTIONS,
  DAY_PRESET_OPTIONS,
  EMPTY_AVAILABILITY,
  SHIFT_DURATION_OPTIONS,
  SHIFT_TYPE_OPTIONS,
  toggleArrayValue,
} from "../../utils/userAvailability";

const availabilityPath = "/users/me/availability";

const AvailabilityModal = ({ isOpen, onClose, onSuccess, userId, subjectName }) => {
  const [form, setForm] = useState({ ...EMPTY_AVAILABILITY });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const getAvailabilityUrl = userId
    ? `${availabilityPath}?userId=${encodeURIComponent(userId)}`
    : availabilityPath;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request(getAvailabilityUrl);
      if (res?.status === 1 && res?.result?.availability) {
        const a = res.result.availability;
        setForm({
          dayPresets: a.dayPresets || [],
          customDays: a.customDays || [],
          shiftDurations: a.shiftDurations || [],
          shiftTypes: a.shiftTypes || [],
        });
      } else {
        setForm({ ...EMPTY_AVAILABILITY });
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load availability");
    } finally {
      setLoading(false);
    }
  }, [getAvailabilityUrl]);

  useEffect(() => {
    if (isOpen) fetchData();
    else setForm({ ...EMPTY_AVAILABILITY });
  }, [isOpen, fetchData]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePreset = (value) => {
    setForm((prev) => {
      let dayPresets = toggleArrayValue(prev.dayPresets, value);
      let customDays = prev.customDays;
      if (value === "CUSTOM" && !dayPresets.includes("CUSTOM")) {
        customDays = [];
      }
      return { ...prev, dayPresets, customDays };
    });
  };

  const toggleCustomDay = (value) => {
    setForm((prev) => {
      const customDays = toggleArrayValue(prev.customDays, value);
      let dayPresets = prev.dayPresets;
      if (customDays.length > 0 && !dayPresets.includes("CUSTOM")) {
        dayPresets = [...dayPresets, "CUSTOM"];
      }
      if (customDays.length === 0) {
        dayPresets = dayPresets.filter((p) => p !== "CUSTOM");
      }
      return { ...prev, customDays, dayPresets };
    });
  };

  const handleSave = async () => {
    const payload = assignUserAvailabilityBodyRequest.parse({
      ...(userId && { userId: Number(userId) }),
      ...form,
    });
    setSaving(true);
    try {
      const response = await request(availabilityPath, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (response?.status === 1) {
        toast.success(response?.message || "Availability updated successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response?.message || "Failed to update availability");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update availability");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const showCustomDays =
    form.dayPresets.includes("CUSTOM") || form.customDays.length > 0;

  return (
    <div
      className={COVERAGE_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="availability-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className={`${COVERAGE_MODAL.panel} max-w-3xl`}>
        <div className={COVERAGE_MODAL.head}>
          <div className="flex items-start gap-3 min-w-0">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <CalendarIcon size={18} />
            </span>
            <div className="min-w-0">
              <h2 id="availability-title" className={COVERAGE_MODAL.title}>
                Availability
              </h2>
              <p className={COVERAGE_MODAL.subtitle}>
                {userId && subjectName
                  ? `Update availability for ${subjectName}`
                  : "Select your availability and preferred shifts"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={COVERAGE_MODAL.close}
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={`${COVERAGE_MODAL.body} space-y-5`}>
          {loading ? (
            <div className="py-16 flex justify-center">
              <SpinnerIcon className="animate-spin text-indigo-600" size={24} />
            </div>
          ) : (
            <>
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    1. Available Days
                  </h3>
                  <button
                    type="button"
                    className={COVERAGE_BTN.link}
                    onClick={() => {
                      const allPresets = DAY_PRESET_OPTIONS.map((o) => o.value);
                      const allDays = CUSTOM_DAY_OPTIONS.map((o) => o.value);
                      const allSelected =
                        form.dayPresets.length === allPresets.length &&
                        form.customDays.length === allDays.length;
                      if (allSelected) {
                        setForm((prev) => ({
                          ...prev,
                          dayPresets: [],
                          customDays: [],
                        }));
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          dayPresets: allPresets,
                          customDays: allDays,
                        }));
                      }
                    }}
                  >
                    Select All
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {DAY_PRESET_OPTIONS.map((opt) => {
                    const checked = form.dayPresets.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                          checked
                            ? "border-indigo-300 bg-indigo-50/70"
                            : "border-indigo-100 bg-white hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePreset(opt.value)}
                          className={`${COVERAGE_CHECKBOX.input} mt-0.5`}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-slate-900">
                            {opt.label}
                          </span>
                          <span className="block text-[11px] text-slate-500">
                            {opt.hint}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {showCustomDays ? (
                  <div className="mt-3 flex flex-wrap gap-3 px-1">
                    {CUSTOM_DAY_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="inline-flex items-center gap-1.5 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.customDays.includes(opt.value)}
                          onChange={() => toggleCustomDay(opt.value)}
                          className={COVERAGE_CHECKBOX.input}
                        />
                        <span className="text-sm text-slate-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                  2. Preferred Shifts
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-indigo-100 p-3 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                      Shift Duration
                    </p>
                    {SHIFT_DURATION_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 cursor-pointer rounded px-1 py-1 hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={form.shiftDurations.includes(opt.value)}
                          onChange={() =>
                            updateField(
                              "shiftDurations",
                              toggleArrayValue(form.shiftDurations, opt.value),
                            )
                          }
                          className={COVERAGE_CHECKBOX.input}
                        />
                        <span className="text-sm text-slate-800">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="rounded-lg border border-indigo-100 p-3 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                      Shift Type
                    </p>
                    {SHIFT_TYPE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-start gap-2 cursor-pointer rounded px-1 py-1 hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={form.shiftTypes.includes(opt.value)}
                          onChange={() =>
                            updateField(
                              "shiftTypes",
                              toggleArrayValue(form.shiftTypes, opt.value),
                            )
                          }
                          className={`${COVERAGE_CHECKBOX.input} mt-0.5`}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm text-slate-800">
                            {opt.label}
                          </span>
                          {opt.hint ? (
                            <span className="block text-[11px] text-slate-500">
                              {opt.hint}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        <div className={COVERAGE_MODAL.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={COVERAGE_BTN.modalCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className={COVERAGE_BTN.modalPrimary}
          >
            {saving ? (
              <>
                <SpinnerIcon className="animate-spin" size={14} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
